import { Component, Output, EventEmitter, Directive, DestroyRef, inject } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';

/**
 * Abstract base class for all manual modals in the application.
 * Provides common functionality for visibility, lifecycle, keyboard handling,
 * and focus management.
 */
@Directive()
export abstract class BaseModalComponent {
  @Output() onClose = new EventEmitter<any>();

  visible = false;
  private previousActiveElement: HTMLElement | null = null;

  /**
   * Common animations for modal entry/exit
   */
  static modalAnimations = [
    trigger('modalAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('150ms ease-in', style({ opacity: 0 }))]),
    ]),
  ];

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cleanup());
  }



  /**
   * Show the modal
   */
  show(): void {
    this.previousActiveElement = document.activeElement as HTMLElement;

    this.visible = true;
    document.body.classList.add('modal-open');

    // Add keyboard listeners
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keydown', this.handleTabKey);

    // Optional: Focus first input element (subclasses can override)
    setTimeout(() => {
      this.focusFirstElement();
    }, 50);
  }

  /**
   * Hide the modal
   */
  hide(): void {
    this.visible = false;
    this.cleanup();

    // Restore focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
  }

  /**
   * Handle keyboard events (ESC key to close)
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.visible) {
      this.close();
    }
  };

  /**
   * Handle Tab key to trap focus within modal
   */
  private handleTabKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab' || !this.visible) return;

    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Shift + Tab on first element -> go to last
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    }
    // Tab on last element -> go to first
    else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  /**
   * Get all focusable elements within the modal
   */
  private getFocusableElements(): HTMLElement[] {
    if (!this.visible) return [];

    const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const modalElements = Array.from(document.querySelectorAll('.modal')) as HTMLElement[];
    const modalElement = modalElements[modalElements.length - 1];

    if (!modalElement) return [];

    const elements = Array.from(modalElement.querySelectorAll(selector)) as HTMLElement[];
    return elements.filter((el) => !el.hasAttribute('disabled'));
  }

  /**
   * Clean up event listeners and body class
   */
  private cleanup(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keydown', this.handleTabKey);
    document.body.classList.remove('modal-open');
  }

  /**
   * Focus the first focusable element in the modal.
   * Subclasses can override this to focus specific elements.
   */
  protected focusFirstElement(): void {
    // Default implementation - subclasses can override
  }

  /**
   * Close the modal (emits false to onClose)
   */
  close(): void {
    this.hide();
    this.onClose.emit(undefined);
  }

  /**
   * Confirm the modal action (emits true to onClose)
   */
  confirm(result: any = true): void {
    this.hide();
    this.onClose.emit(result);
  }
}
